import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CharacterData, 
  ComputedStats, 
  getBrawlerData,
  updateBrawlerData 
} from '../../types/character';
import { EnhancedCharacter } from '../../utils/ClassComponentRegistry';
import Button from '../Common/Button';
import { BRAWLER_STANCES } from '../../data/stances';

interface StancesProps {
  enhancedCharacter: EnhancedCharacter;
}

const Stances: React.FC<StancesProps> = ({ enhancedCharacter }) => {
  if (!enhancedCharacter.character) {
    return (
      <motion.div 
        className="stances"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <div className="stances__loading">
          Loading stances...
        </div>
      </motion.div>
    );
  }

  const character = enhancedCharacter.character;
  
  // Stances are only for Brawler class
  if (character.class?.toLowerCase() !== 'brawler') {
    return null; // Don't render for non-Brawler characters
  }

  const playerTier = character.tier || 1;
  const brawlerData = getBrawlerData(character);
  const learnedStanceIds = brawlerData.martialStances;

  // Only show stances that have been learned in MartialArtist
  const learnedStances = [];
  for (let tier = 1; tier <= playerTier; tier++) {
    if (BRAWLER_STANCES[tier as keyof typeof BRAWLER_STANCES]) {
      const tierStances = BRAWLER_STANCES[tier as keyof typeof BRAWLER_STANCES];
      const learnedTierStances = tierStances.filter(stance => learnedStanceIds.includes(stance.id));
      learnedStances.push(...learnedTierStances);
    }
  }

  const setStance = (stanceId: string) => {
    if (enhancedCharacter.updateClassSpecificData) {
      const currentBrawlerData = getBrawlerData(character);
      enhancedCharacter.updateClassSpecificData({
        brawler: {
          ...currentBrawlerData,
          activeStance: stanceId
        }
      });
    }
  };

  const clearStance = () => {
    if (enhancedCharacter.updateClassSpecificData) {
      const currentBrawlerData = getBrawlerData(character);
      enhancedCharacter.updateClassSpecificData({
        brawler: {
          ...currentBrawlerData,
          activeStance: 'none'
        }
      });
    }
  };

  return (
    <motion.div 
      className="stances"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
        <motion.div 
          className="stances__header"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1, duration: 0.3 }}
        >
          <motion.p 
            className="stances__info"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.15 }}
          >
            Learned Stances: {learnedStances.length} total
          </motion.p>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            whileHover={{ scale: 1.05}}
            whileTap={{ scale: 0.95 }}
          >
            <Button
              variant="CLEAR"
              size="SMALL"
              onClick={clearStance}
            >
              Clear Stance
            </Button>
          </motion.div>
        </motion.div>

        <AnimatePresence mode="wait">
          {learnedStances.length === 0 ? (
            <motion.div 
              key="empty-state"
              className="stances__empty"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ delay: 0.25, duration: 0.3 }}
            >
              <p>No stances learned yet. Visit the Class Features tab to learn stances in the Martial Artist section.</p>
            </motion.div>
          ) : (
            <motion.div
              key="stances-grid"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ delay: 0.25, duration: 0.3 }}
            >
          {[1, 2, 3, 4].map(tier => {
            const allTierStances = BRAWLER_STANCES[tier as keyof typeof BRAWLER_STANCES] || [];
            const learnedTierStances = allTierStances.filter(stance => learnedStanceIds.includes(stance.id));
            
            if (tier > playerTier || learnedTierStances.length === 0) return null;
            
            return (
              <motion.div 
                key={tier} 
                className="stances__tier"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + tier * 0.1, duration: 0.3 }}
                layout
              >
                <motion.h4 
                  className="stances__tier-title"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.35 + tier * 0.1 }}
                >
                  Tier {tier} Stances
                </motion.h4>
                <motion.div 
                  className="stances__grid"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 + tier * 0.1, staggerChildren: 0.1 }}
                >
                  <AnimatePresence>
                    {learnedTierStances.map((stance, stanceIndex) => {
                      const isActive = brawlerData.activeStance === stance.id;
                      return (
                        <motion.div 
                          key={stance.id}
                          className={`stance ${isActive ? 'stance--active' : ''}`}
                          initial={{ opacity: 0, scale: 0.95, y: 20 }}
                          animate={{ 
                            opacity: 1, 
                            scale: 1, 
                            y: 0,
                            boxShadow: isActive
                              ? 'var(--semantic-shadows-focus)'
                              : 'var(--semantic-shadows-low)'
                          }}
                          exit={{ opacity: 0, scale: 0.95, y: -20 }}
                          transition={{ 
                            delay: 0.45 + tier * 0.1 + stanceIndex * 0.05, 
                            duration: 0.3 
                          }}
                          layout
                          whileHover={{ 
                            scale: 1.02, 
                            boxShadow: isActive
                              ? 'var(--semantic-shadows-elevated)'
                              : 'var(--semantic-shadows-medium)'
                          }}
                        >
                          <motion.div 
                            className="stance__header"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.5 + tier * 0.1 + stanceIndex * 0.05 }}
                          >
                            <h5 className="stance__name">{stance.name}</h5>
                            <motion.div
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              <Button
                                variant={isActive ? 'PRIMARY' : 'SECONDARY'}
                                size="SMALL"
                                onClick={() => setStance(stance.id)}
                              >
                                {isActive ? '✨ Active' : '⚡ Activate'}
                              </Button>
                            </motion.div>
                          </motion.div>
                          <motion.p 
                            className="stance__description"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.55 + tier * 0.1 + stanceIndex * 0.05 }}
                          >
                            {stance.description}
                          </motion.p>
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>
                </motion.div>
              </motion.div>
            );
          })}
            </motion.div>
          )}
        </AnimatePresence>
    </motion.div>
  );
};

export default Stances;